import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Select, Spin } from 'antd'
import { AppleFilled, CloseOutlined } from '@ant-design/icons'
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline'
import axios from 'axios'
import clsx from 'clsx'
import { appApiUrls } from '@shared/url-map.js'
import { POSTHOG_PROJECT_URL, USAGE_FILTER_KEYS, USAGE_RANGES, USAGE_TESTFLIGHT_MODES } from '@shared/constants.js'
import type {
  UsageDashboard,
  UsageFilterKey,
  UsageFilters,
  UsageRange,
  UsageTestFlightMode,
} from '@shared/common-types.js'
import BreakdownCard from './BreakdownCard.js'
import FunnelCard from './FunnelCard.js'
import PurchaseSourcesCard from './PurchaseSourcesCard.js'
import UsageChart from './UsageChart.js'
import VersionShareCard from './VersionShareCard.js'
import {
  FILTER_LABELS,
  METRICS,
  RANGE_OPTIONS,
  UNKNOWN_LABEL,
  changeRatio,
  countryFlag,
  countryName,
  formatMetric,
} from './format.js'
import type { UsageMetric } from './format.js'

type OkDashboard = UsageDashboard & { state: 'ok' }

const TESTFLIGHT_OPTIONS: { value: UsageTestFlightMode; label: string }[] = [
  { value: 'exclude', label: '排除 TF' },
  { value: 'include', label: '含 TF' },
  { value: 'only', label: '只看 TF' },
]

type LoadState =
  | { kind: 'loading'; last: OkDashboard | null }
  | { kind: 'requestFailed' }
  | { kind: 'loaded'; dashboard: UsageDashboard }

/** 范围、TestFlight、过滤、选中的指标都放在 URL 里，刷新和转发链接都能保留 */
function useUsageParams() {
  const [searchParams, setSearchParams] = useSearchParams()
  const rangeParam = searchParams.get('range')
  const range: UsageRange = (USAGE_RANGES as readonly string[]).includes(rangeParam ?? '')
    ? (rangeParam as UsageRange)
    : 'today'
  const testflightParam = searchParams.get('testflight')
  const testflight: UsageTestFlightMode = (USAGE_TESTFLIGHT_MODES as readonly string[]).includes(testflightParam ?? '')
    ? (testflightParam as UsageTestFlightMode)
    : 'exclude'
  const metricParam = searchParams.get('metric')
  const metric: UsageMetric = METRICS.some((m) => m.key === metricParam) ? (metricParam as UsageMetric) : 'users'
  const filters: UsageFilters = {}
  for (const key of USAGE_FILTER_KEYS) {
    const value = searchParams.get(key)
    if (value !== null) filters[key] = value
  }

  const update = (mutate: (params: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams)
    mutate(next)
    setSearchParams(next, { replace: true })
  }
  return {
    range,
    testflight,
    metric,
    filters,
    setTestflight: (value: UsageTestFlightMode) =>
      update((p) => (value === 'exclude' ? p.delete('testflight') : p.set('testflight', value))),
    setRange: (value: UsageRange) => update((p) => (value === 'today' ? p.delete('range') : p.set('range', value))),
    setMetric: (value: UsageMetric) => update((p) => (value === 'users' ? p.delete('metric') : p.set('metric', value))),
    setFilter: (key: UsageFilterKey, value: string | undefined) =>
      update((p) => (value === undefined ? p.delete(key) : p.set(key, value))),
  }
}

function useUsageDashboard(range: UsageRange, testflight: UsageTestFlightMode, filters: UsageFilters): LoadState {
  const [state, setState] = useState<LoadState>({ kind: 'loading', last: null })
  const filtersKey = JSON.stringify(filters)
  useEffect(() => {
    const controller = new AbortController()
    setState((prev) => ({
      kind: 'loading',
      last:
        prev.kind === 'loaded' && prev.dashboard.state === 'ok'
          ? prev.dashboard
          : prev.kind === 'loading'
            ? prev.last
            : null,
    }))
    axios
      .get(appApiUrls.usageDashboard, {
        params: { range, testflight, ...JSON.parse(filtersKey) },
        signal: controller.signal,
      })
      .then((res) => {
        setState(res.data.code === 0 ? { kind: 'loaded', dashboard: res.data.data } : { kind: 'requestFailed' })
      })
      .catch((error) => {
        if (axios.isCancel(error)) return
        console.error('Failed to load usage dashboard', error)
        setState({ kind: 'requestFailed' })
      })
    return () => controller.abort()
  }, [range, testflight, filtersKey])
  return state
}

function formatShanghaiClock(iso: string) {
  return new Date(iso).toLocaleTimeString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function ChangeBadge({ ratio }: { ratio: number | null }) {
  if (ratio === null) return <span className="text-xs text-gray-400 sm:text-sm">—</span>
  const up = ratio >= 0
  const Icon = up ? ArrowTrendingUpIcon : ArrowTrendingDownIcon
  return (
    <span
      className={clsx(
        'flex items-center gap-0.5 text-xs font-medium sm:gap-1 sm:text-sm',
        up ? 'text-green-600' : 'text-red-500',
      )}
    >
      <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
      {up ? '+' : ''}
      {Math.round(ratio * 100)}%
    </span>
  )
}

/** 过滤条件，每个都带着常驻的 ✕（不用 hover 才出来） */
function FilterChips({
  filters,
  dashboard,
  onRemove,
}: {
  filters: UsageFilters
  dashboard: OkDashboard | null
  onRemove: (key: UsageFilterKey) => void
}) {
  const entries = USAGE_FILTER_KEYS.filter((key) => filters[key] !== undefined).map(
    (key) => [key, filters[key]!] as const,
  )
  if (entries.length === 0) return null
  const display = (key: UsageFilterKey, value: string) => {
    if (key === 'country') {
      const label = dashboard?.breakdowns.countries.find((row) => row.value === value)?.label ?? value
      return value ? `${countryFlag(value)} ${countryName(value, label)}` : UNKNOWN_LABEL
    }
    return value || UNKNOWN_LABEL
  }
  return (
    <div className="flex flex-wrap gap-2 md:justify-end">
      {entries.map(([key, value]) => (
        <span
          key={key}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white py-0.5 pl-2 pr-0.5 text-xs dark:border-slate-600 dark:bg-slate-700 sm:gap-2 sm:py-1 sm:pl-3 sm:pr-1 sm:text-sm"
        >
          <span className="text-gray-500 dark:text-gray-400">{FILTER_LABELS[key]} =</span>
          <span className="font-semibold">{display(key, value)}</span>
          <button
            type="button"
            aria-label={`去掉${FILTER_LABELS[key]}过滤`}
            onClick={() => onRemove(key)}
            className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-slate-600"
          >
            <CloseOutlined className="text-xs" />
          </button>
        </span>
      ))}
    </div>
  )
}

const integerFormat = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 })

/**
 * 一个指标块显示什么。多天范围里日活和后台唤醒的大字是日均（和图上每天的点、Aptabase 的 Daily Users 同口径），
 * 去重人数放在下面一行小字；后台唤醒在单日范围里小字是「其中没打开过 App 的」。
 */
function tileContent(key: UsageMetric, label: string, dashboard: OkDashboard) {
  const daily = dashboard.dailyAverage
  if (daily && (key === 'users' || key === 'backgroundUsers')) {
    const unique = dashboard.current[key]
    return {
      value: daily.current[key],
      previous: daily.previous[key],
      label: key === 'users' ? '平均日活' : '日均唤醒',
      sub: unique === null ? null : `去重 ${integerFormat.format(unique)} ${key === 'users' ? '人' : '台'}`,
    }
  }
  const backgroundOnly = dashboard.backgroundOnlyUsers.current
  return {
    value: dashboard.current[key],
    previous: dashboard.previous[key],
    label: key === 'users' && dashboard.granularity === 'day' ? '用户' : label,
    sub: key === 'backgroundUsers' && backgroundOnly !== null ? `${integerFormat.format(backgroundOnly)} 未打开` : null,
  }
}

function Body({ dashboard, params }: { dashboard: OkDashboard; params: ReturnType<typeof useUsageParams> }) {
  const rangeOption = RANGE_OPTIONS.find((option) => option.value === dashboard.range)!
  const compareLabel = dashboard.range === 'today' ? '较昨天同时段' : `较${rangeOption.previous}`
  const { breakdowns } = dashboard
  return (
    <>
      <div className="rounded-xl bg-white p-3 dark:bg-slate-700 md:p-4">
        {/* 手机上五个排一行：折成两行要多占小半屏 */}
        <div className="mb-1 grid grid-cols-5 gap-0.5 sm:mb-2 sm:flex sm:flex-wrap sm:gap-2">
          {METRICS.map(({ key, label, title }) => {
            const selected = params.metric === key
            const tile = tileContent(key, label, dashboard)
            const valueText = formatMetric(key, tile.value === null ? null : Math.round(tile.value))
            return (
              <button
                key={key}
                type="button"
                title={title}
                onClick={() => params.setMetric(key)}
                className={clsx(
                  'flex min-w-0 flex-col items-center rounded-lg px-0.5 py-2 transition-colors sm:min-w-[120px] sm:px-4 sm:py-3',
                  selected
                    ? 'bg-blue-50 ring-2 ring-blue-500 dark:bg-blue-500/10'
                    : 'hover:bg-gray-50 dark:hover:bg-slate-600',
                )}
              >
                <span
                  className={clsx(
                    'whitespace-nowrap font-bold tabular-nums dark:text-white sm:text-3xl',
                    // 手机上一格只有 65px 左右，「321,895」这种长数字再小一号
                    valueText.length >= 7 ? 'text-sm' : 'text-base',
                  )}
                >
                  {valueText}
                </span>
                <span className="mt-0.5 flex items-center gap-1.5 whitespace-nowrap text-[11px] text-gray-500 dark:text-gray-400 sm:mt-1 sm:text-sm">
                  {tile.label}
                  {selected && <span className="hidden h-2 w-2 rounded-full bg-blue-500 sm:inline-block" />}
                </span>
                {tile.sub && <span className="whitespace-nowrap text-[10px] text-gray-400 sm:text-xs">{tile.sub}</span>}
                <span
                  className="mt-0.5 sm:mt-1"
                  title={`${compareLabel}：${formatMetric(key, tile.previous === null ? null : Math.round(tile.previous))}`}
                >
                  <ChangeBadge ratio={changeRatio(tile.value, tile.previous)} />
                </span>
              </button>
            )
          })}
        </div>
        <UsageChart
          dashboard={dashboard}
          metric={params.metric}
          currentName={rangeOption.current}
          previousName={rangeOption.previous}
          note={`涨跌${compareLabel}`}
        />
      </div>

      <FunnelCard funnel={dashboard.funnel} compareLabel={compareLabel} previousName={rangeOption.previous} />
      <PurchaseSourcesCard sources={dashboard.purchaseSources} />

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <BreakdownCard
          title="国家"
          countLabel="会话"
          rows={breakdowns.countries}
          total={dashboard.current.sessions}
          selected={params.filters.country}
          onSelect={(value) => params.setFilter('country', value)}
          renderLabel={(row) => (
            <>
              <span>{row.value ? countryFlag(row.value) : '🏳️'}</span>
              <span className="truncate">{countryName(row.value, row.label)}</span>
            </>
          )}
        />
        <BreakdownCard
          title="iOS 版本"
          icon={<AppleFilled />}
          countLabel="会话"
          rows={breakdowns.osVersions}
          total={dashboard.current.sessions}
          selected={params.filters.osVersion}
          onSelect={(value) => params.setFilter('osVersion', value)}
        />
        <BreakdownCard
          title="事件"
          countLabel="次数"
          rows={breakdowns.events}
          selected={params.filters.event}
          onSelect={(value) => params.setFilter('event', value)}
        />
        <BreakdownCard
          title="App 版本"
          countLabel="会话"
          rows={breakdowns.appVersions}
          total={dashboard.current.sessions}
          selected={params.filters.appVersion}
          onSelect={(value) => params.setFilter('appVersion', value)}
        />
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <VersionShareCard
          title="iOS 版本占比"
          icon={<AppleFilled />}
          rows={breakdowns.osMinorVersions}
          groupBy="major"
          prefix="iOS "
          subject="iOS 版本"
        />
        <VersionShareCard
          title="App 版本占比"
          rows={breakdowns.appMinorVersions}
          groupBy="version"
          subject="App 版本"
        />
      </div>
      <p className="px-1 text-xs text-gray-500 dark:text-gray-400">
        数据来自 PostHog，{formatShanghaiClock(dashboard.fetchedAt)} 取数，缓存 3 分钟，时间按北京时间。默认排除
        TestFlight（含本地装的 Release 包），右上角可以切成含 TF 或只看 TF。日活 = 当天有 session_start
        的人（不含后台唤醒）；会话 = 前台会话（30 分钟不活跃切分）；事件 = 前后台所有自定义事件；平均时长 =
        结束过的会话的前台时长平均；后台唤醒 = 被 iOS 在后台拉起过的设备（app_started 之后 60
        秒内没有前台事件），「未打开」是其中这段时间一次都没打开 App
        的。多天范围的日活和后台唤醒是过完的那几天的日均，去重数在小字里。点列表里的一行按它过滤，事件过滤看的是「包含这个事件的会话」，此时没有后台唤醒。
        <a className="ml-1 text-blue-500" href={POSTHOG_PROJECT_URL} target="_blank" rel="noreferrer">
          在 PostHog 查看 ↗
        </a>
      </p>
    </>
  )
}

export default function UsageDashboardPage() {
  const params = useUsageParams()
  const state = useUsageDashboard(params.range, params.testflight, params.filters)
  const dashboard =
    state.kind === 'loaded' && state.dashboard.state === 'ok'
      ? state.dashboard
      : state.kind === 'loading'
        ? state.last
        : null

  return (
    <div className="m-2 flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold">使用概况</h1>
        <div className="flex items-center gap-3">
          {state.kind === 'loading' && <Spin size="small" />}
          <Select
            className="w-24 sm:w-28"
            value={params.testflight}
            onChange={params.setTestflight}
            options={TESTFLIGHT_OPTIONS}
          />
          <Select
            className="w-28 sm:w-36"
            value={params.range}
            onChange={params.setRange}
            options={RANGE_OPTIONS.map(({ value, label }) => ({ value, label }))}
          />
        </div>
      </div>
      <FilterChips
        filters={params.filters}
        dashboard={dashboard}
        onRemove={(key) => params.setFilter(key, undefined)}
      />

      {state.kind === 'requestFailed' && (
        <div className="rounded-xl bg-white p-4 text-red-400 dark:bg-slate-700">加载失败</div>
      )}
      {state.kind === 'loaded' && state.dashboard.state === 'unconfigured' && (
        <div className="rounded-xl bg-white p-4 text-gray-500 dark:bg-slate-700">未配置 POSTHOG_PERSONAL_API_KEY</div>
      )}
      {state.kind === 'loaded' && state.dashboard.state === 'failed' && (
        <div className="rounded-xl bg-white p-4 text-red-400 dark:bg-slate-700">
          PostHog 查询失败：{state.dashboard.reason}
        </div>
      )}
      {dashboard ? (
        <div className={clsx('flex flex-col gap-2 transition-opacity', state.kind === 'loading' && 'opacity-60')}>
          <Body dashboard={dashboard} params={params} />
        </div>
      ) : (
        state.kind === 'loading' && (
          <div className="flex h-64 items-center justify-center rounded-xl bg-white dark:bg-slate-700">
            <Spin />
          </div>
        )
      )}
    </div>
  )
}
